import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Persistent notification manager that lives in Layout.js (never unmounts).
 * Handles:
 *  - Bell sound (AudioContext + bell.mp3) — works on every page, always ready
 *  - Service Worker notification click → navigate to order detail
 *
 * Socket listeners + browser notifications are handled by each dashboard
 * component, which calls the bell via window events.
 *
 * BOTH agent and vendor dashboards emit 'app:play-bell' events that this
 * manager picks up globally.
 */
export default function NotificationManager() {
  const navigate = useNavigate();

  // ── Audio refs ──
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const bellBufferRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const pendingRingRef = useRef(false);

  // ── Ensure AudioContext is ready (user gesture required) ──
  const ensureAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    audioUnlockedRef.current = true;

    if (pendingRingRef.current && bellBufferRef.current) {
      pendingRingRef.current = false;
      try {
        const source = ctx.createBufferSource();
        source.buffer = bellBufferRef.current;
        const gain = ctx.createGain();
        gain.gain.value = 0.8;
        source.connect(gain);
        gain.connect(ctx.destination);
        source.start(0);
      } catch (e) { console.warn('Deferred bell play failed:', e); }
    }

    return ctx;
  }, []);

  // ── Play bell sound once ──
  const playBellSound = useCallback(() => {
    if (!audioUnlockedRef.current) {
      pendingRingRef.current = true;
      return;
    }
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/bell.mp3');
        audioRef.current.volume = 0.8;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        const ctx = ensureAudioContext();
        const buffer = bellBufferRef.current;
        if (!buffer) {
          pendingRingRef.current = true;
          return;
        }
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        const gain = ctx.createGain();
        gain.gain.value = 0.8;
        source.connect(gain);
        gain.connect(ctx.destination);
        source.start(0);
      });
    } catch (err) { console.warn('Unable to play bell sound:', err); }
  }, [ensureAudioContext]);

  // ── Load bell.mp3 and set up audio context on mount ──
  useEffect(() => {
    ensureAudioContext();
    fetch('/bell.mp3')
      .then((res) => res.arrayBuffer())
      .then((arrayBuffer) => {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return audioContextRef.current.decodeAudioData(arrayBuffer);
      })
      .then((buffer) => {
        bellBufferRef.current = buffer;
        if (pendingRingRef.current) {
          pendingRingRef.current = false;
          try {
            const ctx = ensureAudioContext();
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            const gain = ctx.createGain();
            gain.gain.value = 0.8;
            source.connect(gain);
            gain.connect(ctx.destination);
            source.start(0);
          } catch (e) { console.warn('Deferred bell play failed:', e); }
        }
      })
      .catch((err) => console.warn('Failed to load bell sound:', err));

    document.addEventListener('click', ensureAudioContext, { passive: true });
    document.addEventListener('touchstart', ensureAudioContext, { passive: true });

    return () => {
      document.removeEventListener('click', ensureAudioContext);
      document.removeEventListener('touchstart', ensureAudioContext);
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Listen for 'app:play-bell' events from dashboard socket handlers ──
  useEffect(() => {
    const handleBell = () => playBellSound();
    window.addEventListener('app:play-bell', handleBell);
    return () => window.removeEventListener('app:play-bell', handleBell);
  }, [playBellSound]);

  // ── Handle Service Worker notification click → navigate to order detail ──
  // Note: The SW itself handles navigation via client.navigate() using the
  // correct role-based URL (e.g. /vendor/orders or /agent/orders?tab=notifications).
  // This message handler is only for broadcasting the action to open clients
  // (e.g. to dismiss notifications). Navigation is handled by the SW directly.
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || event.data.type !== 'NOTIFICATION_ACTION') return;
      const { action } = event.data;
      // Only handle dismiss actions here — navigation is done by the SW
      if (action === 'dismiss') {
        // Could trigger UI dismiss if needed
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMessage);
  }, []);

  return null; // Renders nothing — pure side effects
}